<script setup lang="ts">
import { computed } from 'vue'

import { formatNumber } from '@/shared/lib'
import { Hint } from '@/shared/ui/Hint'
import { Inline } from '@/shared/ui/Inline'
import { InputNumber } from '@/shared/ui/InputNumber'
import { RadioGroup, RadioButton } from '@/shared/ui/RadioGroup'
import { Slider } from '@/shared/ui/Slider'

import type { SizeUnit } from './types'

/**
 * One measurement: the number and the unit it is read in. Two models rather than one
 * object, because the shapes that store a size disagree about how to spell it —
 * `width` + `widthDimension` in a spot's config, `value` + `unit` in a slider's — and
 * a wrapper that took an object would have to be re-taught each of them.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: number | null
    unit?: SizeUnit
    /** Upper bound in pixels. A percentage is capped at 100 whatever this says. */
    max?: number
    min?: number
    disabled?: boolean
    /** Offer one unit only by passing a single-entry list. */
    units?: readonly SizeUnit[]
    /**
     * Put a scale in front of the number. For a measurement with a real ceiling — an
     * offset of at most 200px, a size read against the screen — the scale is what says
     * how much room is left.
     */
    slider?: boolean
    /** Leave unset to inherit the size from ElConfigProvider */
    size?: 'small' | 'default' | 'large'
  }>(),
  {
    modelValue: null,
    unit: 'px',
    max: 10000,
    min: 0,
    units: () => ['px', '%']
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | null): void
  (e: 'update:unit', value: SizeUnit): void
}>()

const PERCENT_MAX = 100

const max = computed(() => (props.unit === '%' ? PERCENT_MAX : props.max))

/** The scale is labelled at its ends, so the unit is readable without the radio. */
const marks = computed(() => ({
  [props.min]: `${formatNumber(props.min)}${props.unit}`,
  [max.value]: `${formatNumber(max.value)}${props.unit}`
}))

function onUnit(value: string | number | boolean | undefined) {
  const unit = value as SizeUnit

  emit('update:unit', unit)

  // 480 pixels is a size; 480 percent is not. Switching the unit brings the number
  // into the range the new one allows, or the input goes on showing a value that its
  // own control would refuse the moment it is touched.
  if (unit === '%' && (props.modelValue ?? 0) > PERCENT_MAX) {
    emit('update:modelValue', PERCENT_MAX)
  }
}
</script>

<template>
  <Inline
    :gap="8"
    align="center"
    :block="slider"
    :class="{ 'size-input--scaled': slider }"
  >
    <Slider
      v-if="slider"
      class="size-input__slider"
      :model-value="modelValue"
      :min="min"
      :max="max"
      :marks="marks"
      :disabled="disabled"
      :size="size"
      @update:model-value="emit('update:modelValue', $event)"
    />

    <InputNumber
      :model-value="modelValue"
      :min="min"
      :max="max"
      :disabled="disabled"
      :size="size"
      @update:model-value="emit('update:modelValue', $event)"
    />

    <RadioGroup
      v-if="units.length > 1"
      :model-value="unit"
      :disabled="disabled"
      :size="size"
      @update:model-value="onUnit"
    >
      <RadioButton v-for="item in units" :key="item" :value="item">
        {{ item }}
      </RadioButton>
    </RadioGroup>

    <Hint v-else inline>{{ units[0] }}</Hint>
  </Inline>
</template>

<style lang="scss" scoped>
// Element Plus hangs the scale's end labels 15px below the rail, outside the slider's
// own 32px box, so the row reserves the space rather than the slider: a margin there
// would lift the rail off the number field's centre line.
.size-input--scaled {
  padding-bottom: 20px;
}

.size-input__slider {
  flex: 1;
  min-width: 100px;

  // Every scale label is centred on the value it marks, so the two end labels hang half
  // their width outside the rail: in a row of sizes the `0px` was clipped by the
  // column's left edge and the `1000px` ran into the column beside it. The ends are the
  // only labels this component draws, so both are pinned inward.
  :deep(.el-slider__marks-text:first-child) {
    transform: translateX(0);
  }

  :deep(.el-slider__marks-text:last-child) {
    transform: translateX(-100%);
  }
}
</style>
